import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { lastValueFrom } from 'rxjs';
import {
  DropdownFilter,
  FinancialAdminControllerService,
  SubscriberControllerService,
  SubscriberRenewalRefund
} from 'src/app/admin-api';
import {
  FilterChangedEvent,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import { Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { GatewayUtil } from 'src/app/utils/gateway.util';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-upgrade-refunds',
  templateUrl: './upgrade-refunds.component.html',
  styleUrl: './upgrade-refunds.component.scss'
})
export class UpgradeRefundsComponent implements OnInit {
  @ViewChild(TableComponent) table: TableComponent;

  subscriberService = inject(SubscriberControllerService);
  ngxPermissionService = inject(NgxPermissionsService);
  financialAdminService = inject(FinancialAdminControllerService);

  permission = signal(false);
  pending = signal(true);

  cols = computed(() =>
    (this.pending() && this.permission()
      ? [
          new TableColumn.Builder()
            .setHeader('')
            .setField(' ')
            .setFilter(false)
            .setType('checkbox')
            .build()
        ]
      : []
    ).concat([
      new TableColumn.Builder()
        .setHeader('Status')
        .setField('pendingRefund')
        .setType('status')
        .setStatusFunction((r: SubscriberRenewalRefund) => {
          if (r.pendingRefund) return 0;
          return -1;
        })
        .setDisplayFunction((r: SubscriberRenewalRefund) =>
          r.pendingRefund ? 'Pendente' : 'Estornado'
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Data upgrade')
        .setField('dateRenewed')
        .setType('date')
        .setCondition('between')
        .build(),
      new TableColumn.Builder()
        .setHeader('Ticket')
        .setField('ticketId')
        .setType('number')
        .setRouterLink('https://b4asupport.zendesk.com/agent/tickets/')
        .setRouterLinkFieldName('ticketId')
        .build(),
      new TableColumn.Builder()
        .setHeader('Assinatura')
        .setField('boxId')
        .setRouterLink('/users/subscribers/')
        .setRouterLinkFieldName('subscriberId')
        .build(),
      new TableColumn.Builder()
        .setHeader('ID Assinatura')
        .setField('subscriberId')
        .setType('number')
        .setRouterLink('/users/subscribers/')
        .setRouterLinkFieldName('subscriberId')
        .build(),
      new TableColumn.Builder()
        .setHeader('Assinante')
        .setField('personName')
        .setCondition('contains')
        .setRouterLink('/users/person/')
        .setRouterLinkFieldName('personId')
        .build(),
      new TableColumn.Builder()
        .setHeader('CPF')
        .setField('cpf')
        .setFilter(true)
        .setType('inputMaskNumber')
        .setMask('999.999.999-99')
        .setDisplayFunction((r: SubscriberRenewalRefund) =>
          r.cpf ? FormUtil.cpfFormatted(r.cpf) : null
        )
        .build(),
      new TableColumn.Builder().setHeader('Email').setField('email').build(),
      new TableColumn.Builder()
        .setHeader('ID Pagamento')
        .setField('paymentId')
        .setType('number')
        .build(),
      new TableColumn.Builder()
        .setHeader('Valor estorno')
        .setField('amount')
        .setType('currency')
        .setCondition('gte')
        .build(),
      new TableColumn.Builder()
        .setHeader('Edições')
        .setField('refunds')
        .setType('formattedInteger')
        .build(),
      new TableColumn.Builder()
        .setHeader('Gateway')
        .setField('gatewayId')
        .setDisplayFunction((r: SubscriberRenewalRefund) => r.gatewayName)
        .setType('number')
        .build(),
      new TableColumn.Builder()
        .setHeader('ID Transação')
        .setField('transactionId')
        .setRouterLink((r: SubscriberRenewalRefund) =>
          environment.production ||
          [10, 11, 12, 13, 17, 19, 21].includes(r.gatewayId)
            ? GatewayUtil.gatewayUrl[r.gatewayId]?.concat(r.transactionId)
            : null
        )
        .build()
    ])
  );
  refundsCols = computed(() =>
    [
      new TableColumn.Builder()
        .setHeader('Edição')
        .setField('theme')
        .setFilter(false)
        .build(),
      new TableColumn.Builder()
        .setHeader('Valor')
        .setField('amount')
        .setType('currency')
        .setFilter(false)
        .build(),
      new TableColumn.Builder()
        .setHeader('Criação')
        .setField('dateCreated')
        .setType('date')
        .setFilter(false)
        .build()
    ].concat(
      this.pending()
        ? []
        : [
            new TableColumn.Builder()
              .setHeader('Data do Estorno')
              .setField('dateRefunded')
              .setType('date')
              .setFilter(false)
              .build()
          ]
    )
  );

  dropdownFilters: {
    [field: string]: Array<DropdownFilter>;
  } = {
    pendingRefund: [
      { label: 'Pendente', value: 1 },
      { label: 'Estornado', value: 0 }
    ]
  };

  ngOnInit(): void {
    this.ngxPermissionService
      .hasPermission([
        roleAsObject(Role.Full_Administrator).enumValue,
        roleAsObject(Role.Financial).enumValue
      ])
      .then((result) => this.permission.set(result))
      .catch(() => this.permission.set(false));
  }

  filterChanged(event: FilterChangedEvent) {
    if (event.field === 'pendingRefund') {
      this.pending.set(event.value as boolean);
    }
  }

  async refreshTickets(refunds: SubscriberRenewalRefund[]) {
    try {
      LoaderService.showLoader();
      await lastValueFrom(
        this.financialAdminService.refreshRefundTickets(
          refunds.filter((r) => r.ticketId).map((r) => r.ticketId)
        )
      );
      await this.table.refresh(true);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  rowSelectable = (event?: { data: SubscriberRenewalRefund }): boolean => {
    return event?.data?.pendingRefund && event?.data?.ticketId !== null;
  };
}
